import { useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Building2, FileText, Loader2, User } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  AlertDescription,
  Button,
  Label,
  Skeleton,
  Text,
  Textarea,
  useToast,
} from '@musa.tech/design-system';
import type {
  Contestation,
  ContestationAttachment,
  ContestationResolution,
  ContestationStatus,
} from '@/integrations/contestacao/client';
import {
  useContestationThreads,
  useOpenContestationAttachment,
  useSupplementContestation,
  useUploadContestationEvidence,
} from '@/hooks/useContestations';
import { ContestationStatusBadge } from './ContestationStatusBadge';
import { motiveLabel } from './contestationOptions';
import { EvidencePicker, formatFileSize } from './EvidencePicker';
import { DetailField } from '../DetailField';

function formatMoment(value: string): string {
  try {
    return format(parseISO(value), 'dd/MM/yyyy HH:mm');
  } catch {
    return value;
  }
}

/**
 * Quem abriu, como aparece na tela.
 *
 * O rótulo era "Aberta por você" para toda contestação do próprio gerador — o que apaga qual
 * pessoa da empresa abriu, justamente a informação útil quando várias pessoas usam o Portal. O
 * serviço passou a guardar nome e e-mail das claims do token na abertura; sem eles (contestação
 * anterior às colunas) o rótulo antigo volta a valer, porque o `initiated_by` é um UUID e não
 * diz nada a quem lê.
 */
function openedByLabel(contestation: Contestation): { title: string; subtitle?: string } {
  if (contestation.initiated_by_realm !== 'generator') {
    return { title: 'Aberta pela Musa' };
  }
  const name = contestation.initiated_by_name?.trim();
  const email = contestation.initiated_by_email?.trim();
  if (name) return { title: name, subtitle: email || undefined };
  if (email) return { title: email };
  return { title: 'Aberta por você' };
}

function formatMoney(value: number | string): string {
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Status da coleta na `musa-agreements` para "não aconteceu" (`OpsCollectStatusEnum.FAULT`). */
const COLLECT_FAULT = 'FAULT';

/** Chaves que uma correção mexe, na ordem em que a tela as lê. */
const CORRECTION_FIELDS = [
  { key: 'units_collected', label: 'Unidades', format: (v: number | string) => String(Number(v)) },
  { key: 'weight_collected', label: 'Peso', format: (v: number | string) => `${Number(v)} kg` },
  { key: 'value', label: 'Valor', format: formatMoney },
] as const;

/**
 * O que a Musa corrigiu — **de quanto para quanto**.
 *
 * Mostrava só o resultado ("Valor corrigido: R$ 10,00"), que não diz nada sozinho: o gerador
 * contestou justamente porque discordava de um número, e a tela omitia qual. O lado "de" vem de
 * `contested_snapshot`, o retrato do objeto na abertura — depois da correção aplicada nenhuma
 * outra tela conhece mais o valor anterior.
 *
 * Sem retrato (contestação aberta antes da coluna, ou origem sem o campo) mostra só o valor novo,
 * que é o comportamento anterior. Cruza por chave porque `corrections` e `contested_snapshot`
 * compartilham o vocabulário de propósito.
 *
 * `corrections` chega com decimais como **string**, porque o domínio é financeiro — daí a
 * conversão explícita, e não `Number` implícito na interpolação.
 */
function CorrectionFieldRow({ resolution, snapshot }: {
  resolution: ContestationResolution | null | undefined;
  snapshot: Contestation['contested_snapshot'];
}) {
  const corrections = resolution?.corrections;
  if (!corrections) return null;

  const from = snapshot ?? {};
  const changes = CORRECTION_FIELDS.flatMap(({ key, label, format }) => {
    const to = corrections[key];
    if (to == null) return [];
    const previous = from[key];
    return [{ key, label, from: previous == null ? null : format(previous), to: format(to) }];
  });

  // "Coleta não realizada" aceita não corrige número: corrige o **status** da coleta, e sem esta
  // linha a decisão aparecia sem correção nenhuma — o gerador via o pedido e nenhum desfecho.
  const impeded = corrections.status === COLLECT_FAULT;

  if (!changes.length && !impeded) return null;

  return (
    <DetailField
      label="Correção"
      value={
        <span className="inline-flex flex-wrap items-baseline gap-x-1">
          {impeded && <span>Coleta impedida</span>}
          {changes.map((change, i) => (
            <span key={change.key} className="inline-flex items-baseline gap-x-1">
              {(i > 0 || impeded) && <span className="text-neutral-400">·</span>}
              <span>{change.label}</span>
              {change.from !== null && (
                <>
                  <span className="text-error-600 line-through">{change.from}</span>
                  <span className="text-neutral-500">para</span>
                </>
              )}
              <span className="font-semibold">{change.to}</span>
            </span>
          ))}
        </span>
      }
    />
  );
}

interface ThreadEntry {
  key: string;
  side: 'musa' | 'generator';
  title: string;
  at: string;
  body?: string | null;
}

/**
 * A conversa da contestação — pedido de informações da Musa e resposta do gerador.
 *
 * Antes disto o Portal não exibia nada do que a Musa escrevia: o operador pedia informações e o
 * gerador não tinha como ler o pedido, só responder às cegas. A decisão final não mora aqui — tem
 * bloco próprio ("Retorno Musa"), que fica acima desta conversa.
 *
 * `messages: null` é "não consultado" — é o que a listagem devolve. `[]` é "ninguém escreveu
 * ainda", e aí não há thread a mostrar.
 */
function ContestationThread({ detail }: { detail: Contestation | undefined }) {
  const entries = useMemo<ThreadEntry[]>(() => {
    if (!detail) return [];
    return (detail.messages ?? []).map((message) => ({
      key: message.id,
      side: (message.author_type === 'OPERATOR' ? 'musa' : 'generator') as ThreadEntry['side'],
      title: message.author_type === 'OPERATOR' ? 'A Musa pediu informações' : 'Você enviou informações',
      at: message.created_at,
      body: message.content,
    }));
  }, [detail]);

  if (entries.length === 0) return null;

  return (
    <div className="space-y-2">
      {entries.map((entry) => (
        <div key={entry.key} className="flex items-start gap-2">
          <span
            className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
              entry.side === 'musa' ? 'bg-primary-50' : 'bg-neutral-100'
            }`}
          >
            {entry.side === 'musa' ? (
              <Building2 className="h-3 w-3 text-primary-700" />
            ) : (
              <User className="h-3 w-3 text-neutral-500" />
            )}
          </span>
          <div className="flex-1 space-y-0.5">
            <div className="flex flex-wrap items-center gap-2">
              <Text variant="label" size="small" className="text-neutral-900">
                {entry.title}
              </Text>
              <Text variant="body" size="small" className="text-neutral-500">
                {formatMoment(entry.at)}
              </Text>
            </div>
            {entry.body && (
              <Text variant="body" size="small" className="whitespace-pre-wrap text-neutral-800">
                {entry.body}
              </Text>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Os anexos da contestação, abríveis.
 *
 * O arquivo que o gerador envia na abertura só era visível no SFM — quem anexou não tinha como
 * conferir o que subiu. O bucket é privado, então o link é assinado e pedido no clique: guardá-lo
 * daria um botão que funciona por alguns minutos e depois falha calado.
 */
function ContestationAttachments({
  contestationId,
  attachments,
}: {
  contestationId: string;
  attachments: ContestationAttachment[] | undefined;
}) {
  const { toast } = useToast();
  const open = useOpenContestationAttachment();
  const [opening, setOpening] = useState<string | null>(null);

  if (!attachments || attachments.length === 0) return null;

  const handleOpen = async (attachmentId: string) => {
    setOpening(attachmentId);
    try {
      await open.mutateAsync({ contestationId, attachmentId });
    } catch {
      toast({
        title: 'Não foi possível abrir o anexo',
        description: 'Tente novamente em instantes.',
        variant: 'error',
      });
    } finally {
      setOpening(null);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <Text variant="caption" size="xsmall" className="uppercase tracking-wide text-neutral-500">
        {attachments.length === 1 ? 'Anexo' : 'Anexos'}
      </Text>
      <div className="flex flex-wrap gap-2">
        {attachments.map((attachment) => (
          <Button
            key={attachment.id}
            type="button"
            variant="outline"
            size="sm"
            disabled={opening === attachment.id}
            onClick={() => void handleOpen(attachment.id)}
          >
            {opening === attachment.id ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <FileText className="mr-2 h-4 w-4 text-neutral-500" />
            )}
            {attachment.file_name}
            <span className="ml-2 text-neutral-500">
              {formatFileSize(attachment.file_size_bytes)}
            </span>
          </Button>
        ))}
      </div>
    </div>
  );
}

interface SupplementFormProps {
  contestationId: string;
}

/**
 * Resposta do gerador ao pedido de informações — texto **e/ou documento** (RFC 6.5).
 *
 * O anexo sobe **antes** da mensagem, e não depois como na abertura. Aqui o motivo é a ordem dos
 * estados: `supplement` move a contestação de `AGUARDANDO_INFORMAÇÕES` para `EM_ANÁLISE`, e este
 * formulário só existe no primeiro. Enviando a mensagem primeiro, o formulário desmonta na hora e
 * uma falha do upload ficaria sem lugar para ser corrigida. Com o anexo primeiro, falha de upload
 * não muda estado nenhum e o gerador tenta outra vez com o arquivo ainda escolhido.
 *
 * `POST /attachments` não tem gate de status, então anexar em `AGUARDANDO_INFORMAÇÕES` é válido —
 * é o mesmo endpoint da abertura.
 */
function SupplementForm({ contestationId }: SupplementFormProps) {
  const { toast } = useToast();
  const [message, setMessage] = useState('');
  const [evidence, setEvidence] = useState<File | null>(null);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);
  /** Anexo que já subiu — evita duplicar o arquivo quando só a mensagem falhou. */
  const [uploadedEvidence, setUploadedEvidence] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const supplement = useSupplementContestation();
  const uploadEvidence = useUploadContestationEvidence();
  const isPending = supplement.isPending || uploadEvidence.isPending;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (evidence && !uploadedEvidence) {
      try {
        const attachmentId = await uploadEvidence.mutateAsync({
          contestationId,
          file: evidence,
        });
        setUploadedEvidence(attachmentId);
      } catch {
        setError('Não foi possível enviar o anexo. Tente novamente ou envie só o texto.');
        return;
      }
    }

    try {
      await supplement.mutateAsync({ id: contestationId, message: message.trim() });
      setMessage('');
      setEvidence(null);
      setUploadedEvidence(null);
      toast({
        title: 'Informações enviadas',
        description: evidence
          ? 'O texto e o anexo foram enviados. A contestação voltou para análise da Musa.'
          : 'A contestação voltou para análise da Musa.',
        variant: 'success',
      });
    } catch {
      setError('Não foi possível enviar as informações. Tente novamente.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <div className="flex flex-col gap-1">
        <Label htmlFor={`supplement-${contestationId}`}>
          <Text variant="label" size="small" className="text-neutral-700">
            A Musa pediu mais informações
          </Text>
        </Label>
        <Textarea
          id={`supplement-${contestationId}`}
          placeholder="Escreva as informações complementares..."
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          rows={3}
        />
      </div>
      <EvidencePicker
        id={`supplement-evidence-${contestationId}`}
        label={<>Documento complementar <span className="text-neutral-500">(opcional)</span></>}
        file={evidence}
        error={evidenceError}
        disabled={isPending}
        onSelect={(file, selectionError) => {
          setEvidence(file);
          setEvidenceError(selectionError);
          if (!file) setUploadedEvidence(null);
        }}
      />
      {uploadedEvidence && (
        <Text variant="body" size="small" className="text-neutral-500">
          O anexo já subiu — reenviar agora manda só o texto.
        </Text>
      )}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="flex justify-end">
        <Button
          type="submit"
          size="sm"
          disabled={message.trim().length === 0 || !!evidenceError || isPending}
        >
          {isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          Enviar informações
        </Button>
      </div>
    </form>
  );
}

interface ContestationHistoryProps {
  contestations: Contestation[];
  /** Rótulo do objeto contestado, por `object_type:object_id`. */
  describeObject: (contestation: Contestation) => string;
  isLoading?: boolean;
}

/** Valor do único item — o accordion recolhe a seção inteira, não cada contestação. */
const HISTORY_ITEM = 'contestation-history';

/**
 * O card da seção, recolhível. Fica aberto por padrão: quem entra na tela de uma medição
 * contestada quer ver o desfecho — o accordion existe para tirar o bloco do caminho depois de
 * lido, não para escondê-lo de saída.
 *
 * O `<h3>` semântico vem do `Accordion.Header` do Radix, que embrulha o trigger. Por isso o
 * `Text` do título é `span`: `<h3><button><h3>` seria aninhamento inválido.
 */
function HistoryShell({
  status,
  children,
}: {
  status?: ContestationStatus;
  children: React.ReactNode;
}) {
  return (
    <Accordion type="single" collapsible defaultValue={HISTORY_ITEM}>
      <AccordionItem value={HISTORY_ITEM} className="border-0">
        <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
          <AccordionTrigger className="px-4 py-4 hover:no-underline">
            <div className="flex flex-1 flex-wrap items-center justify-between gap-2 pr-2">
              <Text as="span" variant="heading" size="large" className="text-neutral-900">
                Histórico da contestação
              </Text>
              {status && <ContestationStatusBadge status={status} />}
            </div>
          </AccordionTrigger>

          <AccordionContent className="px-4 pb-4 pt-0">{children}</AccordionContent>
        </div>
      </AccordionItem>
    </Accordion>
  );
}

export function ContestationHistory({
  contestations,
  describeObject,
  isLoading,
}: ContestationHistoryProps) {
  const contestationIds = useMemo(() => contestations.map((c) => c.id), [contestations]);
  const threads = useContestationThreads(contestationIds);

  if (isLoading) {
    return (
      <section className="space-y-2">
        <HistoryShell>
          <Skeleton className="h-20 w-full rounded-lg" />
        </HistoryShell>
      </section>
    );
  }

  if (contestations.length === 0) return null;

  /**
   * O status vive na linha do título da seção (decisão de produto). Com mais de uma contestação
   * um badge só ali mentiria sobre as demais — então ele reflete a mais recente (a lista chega
   * ordenada por `created_at` desc) e cada card ganha o próprio, para dar pra distinguir.
   */
  const hasMultiple = contestations.length > 1;
  const sectionStatus = contestations[0]?.status;

  return (
    <section className="space-y-2">
      {/* Um card externo só, igual ao "Resumo financeiro" — sem linha sob o título: o respiro do
          padding já separa. As N contestações ficam empilhadas dentro, divididas só entre si. */}
      <HistoryShell status={sectionStatus}>
        <div className="divide-y divide-neutral-200">
          {contestations.map((contestation) => {
            const openedBy = openedByLabel(contestation);
            const detail = threads.detail(contestation.id);
            const resolution = detail?.resolution;

            return (
              <article key={contestation.id} className="space-y-4 py-4 first:pt-0 last:pb-0">
                {/* Retorno Musa em cima: é a atualização mais recente, e o gerador abre esta tela
                    para saber o desfecho — não para reler o que ele mesmo escreveu. */}
                {resolution && (
                  <div className="space-y-2">
                    <Text variant="label" size="small" className="uppercase tracking-wide text-neutral-500">
                      Retorno Musa
                    </Text>

                    {resolution.resolution_reason && (
                      <DetailField label="Descrição" value={resolution.resolution_reason} />
                    )}
                    {/* `resolution` vem do **detalhe**: a listagem devolve `null` nesse campo.
                        O retrato é campo simples do modelo e vem nas duas, com o detalhe na
                        frente por ser o objeto mais completo. */}
                    <CorrectionFieldRow
                      resolution={resolution}
                      snapshot={detail?.contested_snapshot ?? contestation.contested_snapshot}
                    />
                    <DetailField label="Resolvido em" value={formatMoment(resolution.resolved_at)} />
                  </div>
                )}

                {/* A conversa fica entre a decisão e a abertura — e permanece cronológica por
                    dentro: inverter as falas obrigaria a ler um diálogo de baixo para cima. */}
                <ContestationThread detail={detail} />

                {contestation.status === 'AWAITING_INFO' && (
                  <SupplementForm contestationId={contestation.id} />
                )}

                {/* Contestação — o registro original, na base. */}
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Text variant="label" size="small" className="uppercase tracking-wide text-neutral-500">
                      Contestação
                    </Text>
                    {hasMultiple && <ContestationStatusBadge status={contestation.status} />}
                  </div>

                  <div className="flex flex-wrap gap-x-8 gap-y-2">
                    <DetailField
                      label="Contestador"
                      value={
                        <>
                          {openedBy.title}
                          {openedBy.subtitle && (
                            <>
                              <Text as="span" variant="body" size="small" className="mx-1 text-neutral-400">
                                ·
                              </Text>
                              <Text as="span" variant="body" size="small" className="text-neutral-500">
                                {openedBy.subtitle}
                              </Text>
                            </>
                          )}
                        </>
                      }
                    />
                    <DetailField label="Data e hora" value={formatMoment(contestation.created_at)} />
                  </div>

                  <DetailField label="Objeto contestado" value={describeObject(contestation)} />
                  <DetailField
                    label="Motivo da contestação"
                    value={motiveLabel(contestation.object_type, contestation.contestation_type)}
                  />
                  <DetailField
                    label="Descrição do motivo da contestação"
                    value={contestation.reason}
                  />

                  <ContestationAttachments
                    contestationId={contestation.id}
                    attachments={threads.attachments(contestation.id)}
                  />
                </div>
              </article>
            );
          })}
        </div>
      </HistoryShell>
    </section>
  );
}
